import { Skeleton } from '@material-ui/lab';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Flags } from '@setvi/shared/enums';

import type {
  ProductDetailsExtraTab,
  ProductDetailsExtraTabId
} from 'componentsV2/product/details/content/components/tabs';
import {
  pdvProductQuery,
  usePdvIdentity,
  type PdvFlag
} from 'Services/Query/Pdv';

import { FeedbackCard } from './feedback-list';
import { useStyles } from './styles';

/**
 * The product dialog's Feedback tab. One id for the built-in (rep) tab and the
 * admin queue's own, so a caller passing its version replaces this one.
 */
export const PDV_FEEDBACK_TAB: ProductDetailsExtraTabId = 'extra:pdv-feedback';

const FeedbackTabContent = ({
  flags,
  loading,
  me
}: {
  flags: PdvFlag[];
  loading: boolean;
  me: string;
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <div className={classes.tab}>
        {[0, 1].map(i => (
          <Skeleton
            key={i}
            variant="rect"
            height={120}
            className={classes.tabSkeleton}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={classes.tab}>
      {flags.map(f => (
        <FeedbackCard
          key={f._id ?? `${f.createdAt}-${f.attr}`}
          flag={f}
          me={me}
        />
      ))}
      {!flags.length && (
        <div className={classes.tabEmpty}>
          <strong>No feedback yet</strong>
          <span>
            Reports on this product&apos;s data, and what happened to them, show
            up here.
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Every report on the product, closed ones included — as a tab of the catalogue's
 * product dialog, shown whenever Product Data Verification is on (even with no
 * reports yet). Null while the flag is off.
 */
export const usePdvFeedbackTab = (
  sku?: string
): ProductDetailsExtraTab | null => {
  const ld = useFlags();
  const enabled = Boolean(ld[Flags.PRODUCT_DATA_VERIFICATION]);
  const { id: me } = usePdvIdentity();

  // Same query (and cache entry) as the Report a Data Issue control.
  const { data: raw, isLoading } = useQuery({
    ...pdvProductQuery(sku ?? ''),
    enabled: enabled && Boolean(sku)
  });
  const flags = raw?.flags ?? raw?.openFlags ?? [];

  if (!enabled || !sku) return null;

  return {
    id: PDV_FEEDBACK_TAB,
    label: flags.length ? `Feedback (${flags.length})` : 'Feedback',
    content: <FeedbackTabContent flags={flags} loading={isLoading} me={me} />
  };
};
